import type { QueryKey } from '@tanstack/react-query';

import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../components/generic/Pagination/consts';
import usePaginationState from '../../../components/generic/Pagination/usePaginationState';
import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import type { GetTeamReleaseGroupsParams, GetTeamReleaseGroupsResponse } from '../../../../shared/types/routes/teams';

/**
 * Function to fetch team release groups
 */
function fetchTeamReleaseGroups(teamId: number, params: GetTeamReleaseGroupsParams) {
  const searchParams = new URLSearchParams({
    page: (params?.page ?? DEFAULT_PAGE).toString(),
    pageSize: (params?.pageSize ?? DEFAULT_PAGE_SIZE).toString(),
  });

  if (params.search) {
    searchParams.append('search', params.search);
  }

  if (params.sort) {
    searchParams.append('sort', params.sort);
  }

  if (params.skipPagination) {
    searchParams.append('skipPagination', 'true');
  }

  return get<GetTeamReleaseGroupsResponse>(`/api/teams/${teamId}/release-groups?${searchParams.toString()}`);
}

/**
 * Base query key for team release groups query. This can be used to invalidate ALL pages
 * of team release groups for a given team.
 */
export const getBaseTeamReleaseGroupsQueryKey = (teamId: number): QueryKey =>
  ['teams', teamId, 'release-groups'];

/**
 * Full query key for team release groups query
 */
export const getTeamReleaseGroupsQueryKey = (teamId: number, params: GetTeamReleaseGroupsParams): QueryKey =>
  [...getBaseTeamReleaseGroupsQueryKey(teamId), params];

/**
 * Hook for fetching team release groups. Bring your own pagination state.
 *
 * @param {number} teamId - ID of the team to fetch release groups for
 * @param {GetTeamReleaseGroupsParams} params - Pagination and search parameters
 * @param {boolean} shouldFetch - Whether the query should automatically run
 */
export function useTeamReleaseGroups(
  teamId: number,
  params: GetTeamReleaseGroupsParams,
  shouldFetch: boolean = true,
) {
  return useQuery<GetTeamReleaseGroupsResponse>({
    queryKey: getTeamReleaseGroupsQueryKey(teamId, params),
    queryFn: () => fetchTeamReleaseGroups(teamId, params),
    enabled: shouldFetch,
    // Keeps previous data visible while fetching next page
    keepPreviousData: true,
  });
}

const DEFAULT_INITIAL_PARAMS: GetTeamReleaseGroupsParams = {
  search: '',
  page: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  sort: 'asc',
  skipPagination: false,
};

/**
 * Hook for fetching team release groups with pagination and optional search.
 */
export default function usePaginatedTeamReleaseGroups(
  teamId: number,
  params: Partial<GetTeamReleaseGroupsParams> = DEFAULT_INITIAL_PARAMS,
  shouldFetch: boolean = true,
) {
  const { page, pageSize, setPage, setPageSize } = usePaginationState(params.page, params.pageSize);
  const query = useTeamReleaseGroups(teamId, { page, pageSize, search: params.search, sort: params.sort ?? 'asc', skipPagination: !!params.skipPagination }, shouldFetch);

  return {
    ...query,
    page,
    pageSize,
    setPage,
    setPageSize,
  };
}
